import { Chip, Skeleton, TableCell, TableRow, Theme, useTheme } from '@mui/material';
import dinero from 'dinero.js';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { OnlyFansMetricsWithSubscriber } from '../../../../hooks/useSextforceMetricsCampaignsOverview';
import { MetricCrossSubscriberEarningsResult } from '../../../../hooks/useSextforceMetricsCrossReferenceMetricEarnings';
import { d2f, metricTypeName } from '../../../../utils/common';
import NumberChip from '../../../common/NumberChip';
import SextforceMetricsClaimedTodayChip from './SextforceMetricsClaimedTodayChip';

type Props = {
    metric: OnlyFansMetricsWithSubscriber;
    open: boolean;
    amountType: 'gross' | 'net';
    timezone: string;
    crossPollinationEarningsMap: Map<string, MetricCrossSubscriberEarningsResult | null>;
    crossPollinationLoadingMap: Map<string, boolean>;
};

const SextforceMetricsGroupsTableRowMetricRow = (props: Props) => {
    const { metric, open, amountType, timezone, crossPollinationEarningsMap, crossPollinationLoadingMap } = props;

    const theme: Theme = useTheme();

    const crossPollinationEarnings: MetricCrossSubscriberEarningsResult | undefined = metric.payload?.crossPollinationEarnings;

    const renderCrossPollinationEarnings = (metric: OnlyFansMetricsWithSubscriber) => {
        const isLoading = crossPollinationLoadingMap.get(metric._id);
        const earnings = crossPollinationEarningsMap.get(metric._id);

        if (isLoading) {
            return <Skeleton variant="text" width={'100%'} />;
        }

        if (!earnings) {
            return (
                <Chip
                    label="N/A"
                    color="default"
                    sx={{
                        width: '100%',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        justifyContent: 'flex-end',
                        color: theme.palette.getContrastText(theme.palette.grey[300]),
                        background: theme.palette.grey[300],
                    }}
                />
            );
        }

        const amount = amountType === 'gross' ? d2f(earnings.total) : d2f(earnings.totalNet);

        return (
            <Chip
                label={`$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                color={'success'}
                sx={{
                    width: '100%',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    justifyContent: 'flex-end',
                    color: theme.palette.common.white,
                    background: theme.palette.success.main,
                    '& .MuiChip-root': {},
                }}
            />
        );
    };

    return (
        <TableRow>
            <TableCell scope="row">{metric.subscriber?.username || 'N/A'}</TableCell>
            <TableCell>{metric.payload && metric.payload.createdAt ? moment(metric.payload.createdAt).format('L') : 'N/A'}</TableCell>
            <TableCell>
                <Link
                    to={
                        metric.type === 'promoCampaign'
                            ? `/subscribers/${metric.subscriberId}/services/sextforce/metrics/campaigns/details/${metric._id}?${
                                  metric.payload && !metric.payload.active ? '&includeInactive=true' : ''
                              }`
                            : `/subscribers/${metric.subscriberId}/services/sextforce/metrics/trials/details/${metric._id}?type=${
                                  metric.type
                              }${metric.payload && !metric.payload.active ? '&includeInactive=true' : ''}`
                    }
                    style={{ textDecoration: 'none' }}
                >
                    {metric.payload?.name || 'No Name'}
                </Link>
            </TableCell>
            <TableCell align="center">{metricTypeName(metric.type)}</TableCell>
            <TableCell>
                {open && (
                    <SextforceMetricsClaimedTodayChip
                        metricId={metric._id}
                        metricType={metric.type}
                        startDate={moment().tz(timezone, true).startOf('day').toDate()}
                        endDate={moment().tz(timezone, true).endOf('day').toDate()}
                        timezone={timezone}
                    />
                )}
            </TableCell>
            <TableCell align="right">
                {metric.type === 'trialLinkTrial' && <NumberChip value={metric.payload?.counters?.claimsCount || 0} />}
                {metric.type === 'promoCampaign' && <NumberChip value={metric.payload?.counters?.countSubscribers || 0} />}
            </TableCell>
            <TableCell align="right">
                {metric.type === 'trialLinkTrial' && <NumberChip value={metric.payload?.counters?.clicksCounts || 0} />}
                {metric.type === 'promoCampaign' && <NumberChip value={metric.payload?.counters?.countTransitions || 0} />}
            </TableCell>
            <TableCell
                align="right"
                sx={{
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    textAlign: 'right',
                }}
            >
                {dinero({
                    amount: Math.trunc((metric.payload?.cpf || 0) * 100),
                    currency: 'USD',
                }).toFormat()}
            </TableCell>
            <TableCell align="right">
                <Chip
                    label={dinero({
                        amount: Math.trunc(
                            metric.payload && metric.payload.earningsTotal && typeof metric.payload.earningsTotal !== 'number'
                                ? d2f(metric.payload.earningsTotal) * (amountType === 'net' ? 0.8 : 1) * 100
                                : 0,
                        ),
                        currency: 'USD',
                    }).toFormat()}
                    color={
                        metric.payload && metric.payload.earningsTotal && typeof metric.payload.earningsTotal !== 'number'
                            ? 'success'
                            : 'default'
                    }
                    sx={{
                        width: '100%',
                        fontFamily: 'monospace',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        justifyContent: 'flex-end',
                        color: theme.palette.common.white,
                        background:
                            metric.payload && metric.payload.earningsTotal && typeof metric.payload.earningsTotal !== 'number'
                                ? theme.palette.success.main
                                : theme.palette.grey[300],
                        '& .MuiChip-root': {},
                    }}
                />
            </TableCell>
            <TableCell align="right">{renderCrossPollinationEarnings(metric)}</TableCell>
        </TableRow>
    );
};

export default SextforceMetricsGroupsTableRowMetricRow;
